import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import SwastikSidebar from "./SwastikSidebar.jsx";
import TopNavBar from "./TopNavBar.jsx";
import { ReminderProvider } from "../../context/ReminderContext.jsx";
import ReminderBanner from "../reminders/ReminderBanner.jsx";

/**
 * App shell: fixed Swastik Professional Salon sidebar + scrolling content canvas — the two-pane
 * composition shared by every mockup. Pages own their own page header and
 * toolbar (the mockups put search/filters in the content area, not the shell).
 *
 * Sticky geometry: the desktop rail's wrapper is the flex item that carries
 * `sticky top-0 h-screen`, so it pins to the viewport for the full height of the
 * (taller) content column. The sidebar inside fills that wrapper and owns its own
 * scroll region, which is what keeps the profile/Logout footer from drifting off
 * the bottom of the screen on long pages.
 *
 * The shell also owns the reminder delivery UI: <ReminderBanner /> sits at the
 * top of this layout (inside <ReminderProvider>) so a due appointment reminder
 * is shown in-app the moment the 60s poller raises it — no browser permission
 * required.
 */
export default function AppLayout() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { pathname } = useLocation();

  // Lock the page behind the mobile drawer and let Escape dismiss it — otherwise
  // the body scrolls under the open drawer and the trap feels broken. The
  // media-query guard also drops the drawer (and its scroll lock) if the viewport
  // grows to the desktop rail, where the overlay is `lg:hidden` and would
  // otherwise leave the page stuck.
  useEffect(() => {
    if (!mobileNavOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") setMobileNavOpen(false);
    };
    document.addEventListener("keydown", onKeyDown);

    const desktop = window.matchMedia("(min-width: 1024px)");
    const onBreakpointChange = (event) => {
      if (event.matches) setMobileNavOpen(false);
    };
    desktop.addEventListener("change", onBreakpointChange);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
      desktop.removeEventListener("change", onBreakpointChange);
    };
  }, [mobileNavOpen]);

  return (
    <ReminderProvider>
      <div className="flex min-h-screen bg-surface">
        <div className="sticky top-0 hidden h-screen lg:block">
          <SwastikSidebar />
        </div>

        {mobileNavOpen && (
          <div className="fixed inset-0 z-50 flex lg:hidden">
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setMobileNavOpen(false)}
              className="absolute inset-0 animate-fade-in bg-inverse-surface/50 backdrop-blur-sm"
            />
            <div className="relative h-full animate-slide-in-left motion-reduce:animate-none">
              <SwastikSidebar onNavigate={() => setMobileNavOpen(false)} />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {/* Pinned over the shell's top edge (fixed positioning, so DOM order
              here only establishes it as the layout's first visual band): the
              reminder drawer. It lives inside <ReminderProvider> for the shared
              poller, and covers the viewport top whenever a reminder is due. */}
          <ReminderBanner />

          <TopNavBar onOpenNav={() => setMobileNavOpen(true)} />

          <main className="flex-1 bg-surface">
            {/* Keyed by pathname so every route change plays a short fade-in. */}
            <div
              key={pathname}
              className="mx-auto max-w-[1280px] animate-fade-in p-6 lg:p-10"
            >
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </ReminderProvider>
  );
}
